import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useGetSeoSettingsQuery } from '../../features/api/apiSlice';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_KEYWORDS,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  SITE_NAME,
  SITE_URL,
  truncateMeta,
  withSiteName,
} from '../../lib/seo';

const SEO = ({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  canonicalUrl,
  schema,
  robots = 'index,follow,max-image-preview:large',
  ogType = 'website',
}) => {
  const { data: globalSeoSettings } = useGetSeoSettingsQuery();

  const defaultTitle = globalSeoSettings?.default_title || DEFAULT_TITLE;
  const defaultDescription = globalSeoSettings?.default_description || DEFAULT_DESCRIPTION;
  const defaultKeywords = globalSeoSettings?.default_keywords || DEFAULT_KEYWORDS;

  const pageTitle = title ? withSiteName(title) : withSiteName(defaultTitle);
  const pageDescription = truncateMeta(description || defaultDescription);
  const pageKeywords = keywords || defaultKeywords;
  const pageOgTitle = ogTitle ? withSiteName(ogTitle) : pageTitle;
  const pageOgDescription = truncateMeta(ogDescription || pageDescription);
  const pageOgImage = ogImage || globalSeoSettings?.default_og_image || DEFAULT_OG_IMAGE;
  const canonical = canonicalUrl || (typeof window !== 'undefined' ? window.location.href : SITE_URL);

  return (
    <Helmet>
      <html lang="en-UG" />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <meta name="author" content={SITE_NAME} />
      {canonical && <link rel="canonical" href={canonical} />}

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_UG" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={pageOgTitle} />
      <meta property="og:description" content={pageOgDescription} />
      {pageOgImage && <meta property="og:image" content={pageOgImage} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={pageOgTitle} />
      <meta name="twitter:description" content={pageOgDescription} />
      {pageOgImage && <meta name="twitter:image" content={pageOgImage} />}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
